import { AI_V2_FUNCTION_PATH, checkAiV2Response, type AiV2AnyRequest, type AiV2Request, type AiV2Response } from '../../supabase/functions/_shared/aiV2Protocol';
import { getSupabase } from './supabaseAdapter';

// Client transport to the AI proxy (Owner task book 2026-09-04 Round 2 §J / §R / §AH).
//   - The request carries the signed-in user's JWT; no provider key exists in this bundle.
//   - XMLHttpRequest, not fetch: the upload-complete event is the ONLY real signal that separates
//     "uploading" from "analyzing", and the Owner asked for real states, not fake progress.
//   - Every outcome is normalized to a code the UI can translate; raw messages never reach the
//     inspector.
export type AiTransportPhase = 'uploading' | 'analyzing';

export type AiTransportFailure = 'offline' | 'network' | 'timeout' | 'aborted' | 'notConfigured' | 'malformedResponse';

export type AiTransportResult = { kind: 'response'; response: AiV2Response; httpStatus: number } | { kind: 'failed'; code: AiTransportFailure; message: string };

export interface AiTransportOptions {
  accessToken: string | null;
  onPhase?: (phase: AiTransportPhase, uploadPct: number) => void;
  signal?: AbortSignal;
  timeoutMs?: number;
}

export type AiTransport = (request: AiV2Request, options: AiTransportOptions) => Promise<AiTransportResult>;
// 2026-09-14: the same wire for the report wording (no photographs). The real transport posts any
// protocol request; the test override for photo runs stays typed on photo requests.
export type AiAnyTransport = (request: AiV2AnyRequest, options: AiTransportOptions) => Promise<AiTransportResult>;

// The function answers within the provider timeout (120 s) plus upload time.
export const AI_CLIENT_TIMEOUT_MS = 170_000;

export function aiV2Endpoint(): string | null {
  const base = ((import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '').trim().replace(/\/$/, '');
  return base.length > 0 ? `${base}${AI_V2_FUNCTION_PATH}` : null;
}

let configuredForTests: boolean | null = null;

export function aiBackendConfigured(): boolean {
  if (configuredForTests !== null) return configuredForTests;
  return aiV2Endpoint() !== null && ((import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '').trim().length > 0;
}

export function setAiBackendConfiguredForTests(value: boolean | null): void {
  configuredForTests = value;
}

let accessTokenForTests: string | null = null;

export function setAccessTokenForTests(token: string | null): void {
  accessTokenForTests = token;
}

// The signed-in user's bearer token (null when there is no live session, e.g. offline continuation).
export async function currentAccessToken(): Promise<string | null> {
  if (accessTokenForTests !== null) return accessTokenForTests;
  const client = getSupabase();
  if (!client) return null;
  try {
    const { data } = await client.auth.getSession();
    return data.session?.access_token ?? null;
  } catch {
    return null;
  }
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

// What a NON-protocol answer means to the inspector. Owner device test 2026-09-12: the platform
// killed the function at its wall-clock limit and answered 546; the app called that "the server's
// answer could not be parsed", which sent the inspector retrying instead of reducing photos.
//   408 / 504 / 524 / 546  the request ran out of time somewhere between gateway and function → timeout
//   502 / 503 / 52x        the gateway had no working function to talk to → network (server unreachable)
//   anything else          truly unexpected content → malformedResponse
export function platformFailureCode(status: number): AiTransportFailure {
  if (status === 408 || status === 504 || status === 524 || status === 546) return 'timeout';
  if (status === 502 || status === 503 || status === 520 || status === 521 || status === 522 || status === 523) return 'network';
  return 'malformedResponse';
}

export const xhrAiTransport: AiAnyTransport = (request, options) =>
  new Promise((resolve) => {
    const endpoint = aiV2Endpoint();
    const anon = ((import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '').trim();
    if (!endpoint || anon.length === 0) {
      resolve({ kind: 'failed', code: 'notConfigured', message: 'AI backend is not configured' });
      return;
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      resolve({ kind: 'failed', code: 'offline', message: 'device is offline' });
      return;
    }
    if (typeof XMLHttpRequest === 'undefined') {
      resolve({ kind: 'failed', code: 'notConfigured', message: 'no XMLHttpRequest in this runtime' });
      return;
    }
    const xhr = new XMLHttpRequest();
    let settled = false;
    const finish = (result: AiTransportResult) => {
      if (settled) return;
      settled = true;
      options.signal?.removeEventListener('abort', onAbort);
      resolve(result);
    };
    const onAbort = () => {
      try {
        xhr.abort();
      } catch {
        // already closed
      }
      finish({ kind: 'failed', code: 'aborted', message: 'cancelled' });
    };
    if (options.signal) {
      if (options.signal.aborted) {
        finish({ kind: 'failed', code: 'aborted', message: 'cancelled' });
        return;
      }
      options.signal.addEventListener('abort', onAbort);
    }
    xhr.open('POST', endpoint, true);
    xhr.timeout = options.timeoutMs ?? AI_CLIENT_TIMEOUT_MS;
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('apikey', anon);
    if (options.accessToken) xhr.setRequestHeader('Authorization', `Bearer ${options.accessToken}`);
    xhr.upload.onprogress = (event) => {
      const pct = event.lengthComputable && event.total > 0 ? Math.min(99, Math.round((event.loaded / event.total) * 100)) : 0;
      options.onPhase?.('uploading', pct);
    };
    xhr.upload.onload = () => options.onPhase?.('analyzing', 100);
    xhr.ontimeout = () => finish({ kind: 'failed', code: 'timeout', message: 'request timed out' });
    xhr.onerror = () => finish({ kind: 'failed', code: 'network', message: 'network error' });
    xhr.onabort = () => finish({ kind: 'failed', code: 'aborted', message: 'cancelled' });
    xhr.onload = () => {
      const parsed = checkAiV2Response(parseJson(xhr.responseText));
      if (!parsed) {
        // A gateway / platform answer that is not our protocol (e.g. a 404 when the function is not
        // deployed, or a 5xx HTML page). Never shown raw.
        finish({ kind: 'failed', code: platformFailureCode(xhr.status), message: `unexpected answer (HTTP ${xhr.status})` });
        return;
      }
      finish({ kind: 'response', response: parsed, httpStatus: xhr.status });
    };
    options.onPhase?.('uploading', 0);
    xhr.send(JSON.stringify(request));
  });

let override: AiTransport | null = null;

export function setAiTransportForTests(transport: AiTransport | null): void {
  override = transport;
}

export function getAiTransport(): AiTransport {
  return override ?? xhrAiTransport;
}

let wordingOverride: AiAnyTransport | null = null;

export function setAiWordingTransportForTests(transport: AiAnyTransport | null): void {
  wordingOverride = transport;
}

// The transport for the report wording: the wording fake when a test installed one; never the
// network while a photo fake is installed (a test that faked the photo run must not reach a server).
export function getAiWordingTransport(): AiAnyTransport {
  if (wordingOverride) return wordingOverride;
  if (override) return async () => ({ kind: 'failed', code: 'notConfigured', message: 'no wording transport installed for tests' });
  return xhrAiTransport;
}

let sortOverride: AiAnyTransport | null = null;

export function setAiSortTransportForTests(transport: AiAnyTransport | null): void {
  sortOverride = transport;
}

// 2026-10-05 — the transport for the photo-pool sort (src/state/poolAiStore.ts), same wire, same rule:
// the sort fake when one is installed; never the network while a photo fake is installed.
export function getAiSortTransport(): AiAnyTransport {
  if (sortOverride) return sortOverride;
  if (override) return async () => ({ kind: 'failed', code: 'notConfigured', message: 'no sort transport installed for tests' });
  return xhrAiTransport;
}
